import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button } from '../ui/Button';
import { Label } from '../ui/Label';
import { Textarea } from '../ui/Textarea';
import { Modal, touch } from './Modal';
import { OrderHistoryDialog } from './OrderHistoryDialog';
import {
  apiMessage,
  fmtEur,
  fmtTime,
  receivingApi,
  type Anomaly,
  type AnomalyRegister,
} from '../../lib/receiving';
import { cn } from '../../utils/cn';

const DEFAULT_OWNER: Record<string, string> = {
  COUNTER_AGENT: 'COUNTER',
  PURCHASER: 'PURCHASING',
  WAREHOUSE_KEEPER: 'WAREHOUSE',
};

/**
 * The anomaly register (spec §8): one list, seen from each desk with the owner filter preset to
 * the person's role. They see the rest but only close what is theirs — the server decides.
 * A missing part never closes by hand: it points to the call list instead.
 */
export function AnomalyRegisterView({
  role,
  callListPath,
}: {
  role: string;
  callListPath?: string;
}) {
  const { t } = useTranslation();
  const [owner, setOwner] = useState<string>(DEFAULT_OWNER[role] ?? 'ALL');
  const [customerType, setCustomerType] = useState('ALL');
  const [status, setStatus] = useState('TO_HANDLE');
  const [reg, setReg] = useState<AnomalyRegister | null>(null);
  const [error, setError] = useState('');
  const [resolving, setResolving] = useState<Anomaly | null>(null);
  const [history, setHistory] = useState<{ id: string; number: string } | null>(null);

  const load = useCallback(() => {
    receivingApi
      .anomalies({ owner, status, customerType: customerType === 'ALL' ? undefined : customerType })
      .then(setReg)
      .catch((err) => setError(apiMessage(err)));
  }, [owner, status, customerType]);
  useEffect(() => {
    load();
  }, [load]);

  const chip = (active: boolean) =>
    cn(
      'min-h-12 rounded-lg border px-4 text-base',
      active ? 'border-primary bg-primary text-primary-foreground' : 'bg-background hover:bg-accent'
    );
  const statusLabel = (s: string) => t(`rcv.ano.status.${s}`);

  return (
    <div className="space-y-4">
      {reg && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
          {(
            [
              ['open', reg.counters.open],
              ['missing', reg.counters.missing],
              ['escalated', reg.counters.escalated],
              ['garages', reg.counters.garages],
              ['consumers', reg.counters.consumers],
            ] as const
          ).map(([k, v]) => (
            <div key={k} className="rounded-xl border bg-card px-4 py-3">
              <div className="text-3xl font-bold tabular-nums">{v}</div>
              <div className="text-sm text-muted-foreground">{t(`rcv.ano.counters.${k}`)}</div>
            </div>
          ))}
        </div>
      )}
      <div
        className="flex flex-wrap items-center gap-2"
        role="group"
        aria-label={t('rcv.ano.filters')}
      >
        <span className="text-sm text-muted-foreground">{t('rcv.ano.customer')}</span>
        {['ALL', 'B2B', 'B2C'].map((v) => (
          <button
            key={v}
            type="button"
            className={chip(customerType === v)}
            onClick={() => setCustomerType(v)}
          >
            {t(`rcv.ano.ct.${v}`)}
          </button>
        ))}
        <span className="ml-3 text-sm text-muted-foreground">{t('rcv.ano.owner')}</span>
        {['ALL', 'COUNTER', 'PURCHASING', 'WAREHOUSE', 'MANAGER'].map((v) => (
          <button key={v} type="button" className={chip(owner === v)} onClick={() => setOwner(v)}>
            {t(`rcv.ano.ownerLabel.${v}`)}
          </button>
        ))}
        <span className="ml-3 text-sm text-muted-foreground">{t('rcv.ano.state')}</span>
        {['TO_HANDLE', 'ALL'].map((v) => (
          <button key={v} type="button" className={chip(status === v)} onClick={() => setStatus(v)}>
            {t(`rcv.ano.st.${v}`)}
          </button>
        ))}
      </div>
      {error && <p className="text-destructive">{error}</p>}
      <div className="overflow-x-auto rounded-xl border">
        <table className="w-full text-base">
          <thead className="bg-muted/50 text-left text-sm text-muted-foreground">
            <tr>
              <th className="px-3 py-2">{t('rcv.ano.col.number')}</th>
              <th className="px-3 py-2">{t('rcv.ano.col.type')}</th>
              <th className="px-3 py-2">{t('rcv.ano.col.customer')}</th>
              <th className="px-3 py-2">{t('rcv.ano.col.order')}</th>
              <th className="px-3 py-2">{t('rcv.ano.col.part')}</th>
              <th className="px-3 py-2">{t('rcv.ano.col.open')}</th>
              <th className="px-3 py-2">{t('rcv.ano.col.owner')}</th>
              <th className="px-3 py-2">{t('rcv.ano.col.next')}</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {reg?.data.length === 0 && (
              <tr>
                <td colSpan={9} className="px-3 py-6 text-center text-muted-foreground">
                  {t('rcv.ano.none')}
                </td>
              </tr>
            )}
            {reg?.data.map((a) => {
              const closed = a.status === 'RESOLVED' || a.status === 'CANCELLED';
              return (
                <tr key={a.id} className="border-t align-top">
                  <td className="px-3 py-3">
                    <div className="whitespace-nowrap font-semibold">{a.number}</div>
                    <span
                      className={cn(
                        'rounded px-1.5 py-0.5 text-xs font-semibold',
                        closed
                          ? 'bg-muted text-muted-foreground'
                          : a.escalated
                            ? 'bg-destructive/15 text-destructive'
                            : 'bg-amber-500/15 text-amber-800 dark:text-amber-300'
                      )}
                    >
                      {statusLabel(a.status)}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <div className="font-semibold">
                      {t(`rcv.ano.type.${a.type}`, { defaultValue: a.typeLabel })}
                    </div>
                    {a.reportsCount > 1 && (
                      <div className="text-xs font-semibold text-destructive">
                        {t('rcv.ano.reports', { count: a.reportsCount })}
                      </div>
                    )}
                    {a.escalated && (
                      <div className="text-xs text-destructive">{t('rcv.ano.escalated')}</div>
                    )}
                  </td>
                  <td className="px-3 py-3">
                    {a.customerName ? (
                      <>
                        {a.customerName}
                        <div className="text-xs text-muted-foreground">
                          {a.customerType === 'B2B'
                            ? `${t('rcv.garage')} · ${t('rcv.exposureOf', { e: fmtEur(a.exposure), l: a.creditLimit == null ? '—' : fmtEur(a.creditLimit) })}`
                            : `${t('rcv.consumer')}${a.paymentMethod ? ` · ${a.paymentMethod}` : ''}${a.depositInvoice ? ` · ${a.depositInvoice}` : ''}`}
                        </div>
                      </>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-3 py-3">
                    {a.orderId ? (
                      <button
                        type="button"
                        className="min-h-10 whitespace-nowrap rounded px-1 font-semibold underline-offset-2 hover:underline"
                        onClick={() => setHistory({ id: a.orderId!, number: a.orderNumber ?? '' })}
                      >
                        {a.orderNumber}
                      </button>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-3 py-3">
                    {a.productName ?? a.code ?? '—'}
                    {a.qty ? ` × ${a.qty}` : ''}
                    {a.supplierName && (
                      <div className="text-xs text-muted-foreground">{a.supplierName}</div>
                    )}
                  </td>
                  <td className="px-3 py-3">
                    {a.daysOpen > 0 ? t('rcv.ano.days', { count: a.daysOpen }) : t('rcv.ano.today')}
                    <div className="text-xs text-muted-foreground">
                      {t('rcv.ano.openedBy', {
                        time: fmtTime(a.openedAt),
                        name: a.openedBy ?? t('rcv.system'),
                      })}
                    </div>
                  </td>
                  <td className="px-3 py-3">{t(`rcv.ano.ownerLabel.${a.owner}`)}</td>
                  <td className="px-3 py-3">
                    {closed ? (
                      <>
                        <span className="text-emerald-700 dark:text-emerald-400">
                          {a.resolution}
                        </span>
                        <div className="text-xs text-muted-foreground">
                          {t('rcv.ano.closedBy', {
                            name: a.closedBy ?? t('rcv.system'),
                            time: fmtTime(a.closedAt),
                          })}
                        </div>
                      </>
                    ) : (
                      a.nextAction
                    )}
                  </td>
                  <td className="px-3 py-3">
                    {!closed && a.type === 'MISSING_PART' && callListPath && (
                      <Link
                        to={callListPath}
                        className="inline-flex min-h-12 items-center rounded-md border px-3 text-sm hover:bg-accent"
                      >
                        {t('rcv.ano.toCallList')}
                      </Link>
                    )}
                    {!closed && a.type !== 'MISSING_PART' && (
                      <Button
                        variant="outline"
                        className="min-h-12"
                        onClick={() => setResolving(a)}
                      >
                        {t('rcv.ano.close')}
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <ResolveDialog
        anomaly={resolving}
        onClose={() => setResolving(null)}
        onDone={() => {
          setResolving(null);
          load();
        }}
      />
      <OrderHistoryDialog
        orderId={history?.id ?? null}
        orderNumber={history?.number ?? null}
        onClose={() => setHistory(null)}
      />
    </div>
  );
}

function ResolveDialog({
  anomaly,
  onClose,
  onDone,
}: {
  anomaly: Anomaly | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const [how, setHow] = useState('');
  const [cancelled, setCancelled] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    setHow('');
    setCancelled(false);
    setError('');
  }, [anomaly?.id]);
  if (!anomaly) return null;
  const submit = async () => {
    try {
      await receivingApi.resolveAnomaly(
        anomaly.id,
        how,
        anomaly.type === 'SUPPLIER_CANCEL' ? cancelled : undefined
      );
      onDone();
    } catch (err) {
      setError(apiMessage(err));
    }
  };
  return (
    <Modal
      open
      title={t('rcv.ano.closeTitle', { number: anomaly.number })}
      subtitle={`${t(`rcv.ano.type.${anomaly.type}`, { defaultValue: anomaly.typeLabel })} · ${anomaly.nextAction ?? ''}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" className={touch} onClick={onClose}>
            {t('rcv.cancel')}
          </Button>
          <Button className={touch} disabled={how.trim().length < 10} onClick={() => void submit()}>
            {t('rcv.ano.close')}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Label htmlFor="how" className="text-base">
          {t('rcv.ano.whatWasDone')}
        </Label>
        <Textarea
          id="how"
          rows={3}
          className="text-base"
          value={how}
          onChange={(e) => setHow(e.target.value)}
        />
        {anomaly.type === 'SUPPLIER_CANCEL' && (
          <label className="flex min-h-12 items-center gap-3 text-base">
            <input
              type="checkbox"
              className="h-6 w-6"
              checked={cancelled}
              onChange={(e) => setCancelled(e.target.checked)}
            />
            {t('rcv.ano.supplierConfirmed')}
          </label>
        )}
        {error && (
          <p className="text-destructive" role="alert">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
